import {Component, OnInit} from '@angular/core';
import {UserModel} from '@shared/models/user.model';
import {QueryProvider} from '@shared/queries/query-provider';
import {SnackBarService} from '@core/services/snack-bar.service';
import {SecurityState} from '@shared/store/reducers/security.reducer';
import {getQueryResponse} from '@shared/store/selectors/query.selector';
import {IQueryState} from '@shared/store/reducers/query.reducer';
import {select, Store} from '@ngrx/store';
import {ActivatedRoute, Router} from '@angular/router';
import {BreadcrumbModel} from '@shared/models/breadcrumb.model';
import {Configuration} from '@shared/models/configuration';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {StringUtility} from '@shared/utility/string-utility';
import {FileManagerService} from '../../services/file-manager.service';
import {AddFolderDialogComponent} from '../dialog-components/add-folder-dialog/add-folder-dialog.component';
import {take, takeUntil} from 'rxjs/operators';
import {DownloadFileAction, UploadFileByParentAction} from '../../store/actions/file-manager.actions';
import {MatDialog} from '@angular/material/dialog';
import {Subject} from 'rxjs';
import {CommandService} from '@core/services/command.service';
import {
  CancelCheckoutFileCommand,
  CheckinCheckoutFileCommand,
  CreateDirectoryCommand,
  DownloadFileCommand,
  RenameDirectoryEntryCommand,
  ShareFileCommand
} from '../../commands/create-directory.command';
import {CheckInDialogComponent} from '../dialog-components/check-in-dialog/check-in-dialog.component';
import {CheckOutDialogComponent} from '../dialog-components/check-out-dialog/check-out-dialog.component';
import {UploadFileDialogComponent} from '../dialog-components/upload-file-dialog/upload-file-dialog.component';
import {FileDialogComponent} from '../file-dialog/file-dialog.component';
import {DialogService} from '@shared/services/dialog-service';
import {DialogModel} from '@shared/models/dialog.model';
import {DeleteConfirmDialogComponent} from '../dialog-components/delete-confirm-dialog/delete-confirm-dialog.component';
import {FileManagementService} from '@core/services/file-management.service';
import {getUserInfo} from '@shared/store/selectors/security.selector';
import {fileOrFolderNameMaxLength} from '../../file-manager.constants';
import {MemberDialogModel, ShareItemService} from '@shared/services/share-item.service';
import {DriveFormModel} from '../../../translation/models/translation.model';
import {TranslationService} from '../../../translation/services/translation.service';
import {GridValueChangeEvent} from '@shared/models/common.models';
import {PlatformCommand} from '@shared/commands/command-base';

@Component({
    standalone: false,
    selector: 'particle-drive',
  templateUrl: './drive.component.html',
  styleUrls: ['./drive.component.scss']
})
export class DriveComponent implements OnInit {

  breadcrumbs: BreadcrumbModel[];
  breadcrumbs$: any;
  breadcrumbKey: string;
  driveId: string;
  folderId: string;
  parentId: string;
  sourceKey: string;
  invalidateSubject: InvalidateSubject;
  public ngUnsubscribe = new Subject<void>();
  sharedDrivesUrl: string;
  createdBy: UserModel;
  defualtBoardPermisssion = {
    'title': 'No Access',
    'description': 'Cannot access any ticket(s)',
    'permissionType': 0
  };

  constructor(protected store: Store<IQueryState>, protected activatedRoute: ActivatedRoute,
              protected dialog: MatDialog, protected commandService: CommandService,
              protected router: Router,
              protected dialogService: DialogService,
              protected fileManagementService: FileManagementService,
              protected snackBarService: SnackBarService,
              protected fileManagerService: FileManagerService,
              protected securityStore: Store<SecurityState>,
              protected shareItemService: ShareItemService,
              protected translationService: TranslationService) {
    this.sourceKey = 'VirtualClientDirectoryEntry';
    this.breadcrumbKey = 'breadcrumb';
    this.invalidateSubject = new InvalidateSubject();
    this.sharedDrivesUrl = 'team-drives';
  }

  ngOnInit() {
    this.activatedRoute.params.subscribe((params: any) => {
      this.driveId = params.id;
      this.folderId = params.folderId;
      let id = this.driveId;
      if (this.folderId) {
        id = this.folderId;
      }
      const config = Configuration.getSmartGridConfig(this.sourceKey);
      config.Parameters['PARENT_ID'] = id;
      this.parentId = id;
      this.breadcrumbs$ = this.store.pipe(select(getQueryResponse, id));
      this.breadcrumbs$.subscribe(state => {
        if (state) {
          this.breadcrumbs = state.breadcrumb;
        }
      });
      this.invalidateSubject.invalidate(id);
      QueryProvider.invokeQueryByExecutionId(this.store, this.breadcrumbKey, id, {'[ID]': id});
    });
    this.securityStore.pipe(select(getUserInfo)).subscribe(u => {
      this.createdBy = u;
    });
  }

  addFolder() {
    const dialogRef = this.dialog.open(AddFolderDialogComponent, {
      minWidth: '600px',
      maxWidth: '600px',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(takeUntil(this.ngUnsubscribe)).subscribe((result: string) => {
      if (result) {
        this.commandService.executeCommand(this.createDirectoryCommand(result.trim(), this.parentId)).pipe().subscribe(data => {
          this.navigateByUrl(`${data.Payload.DriveId}/${data.Payload.Id}`);
        });
      }
    });
  }

  navigateByUrl(relativeUrl: string) {
    this.router.navigateByUrl(`drive/${relativeUrl}`);
  }

  createDirectoryCommand(name: string, parentId: string) {
    return new CreateDirectoryCommand(name, parentId);
  }

  uploadFile() {
    const dialogRef = this.dialog.open(UploadFileDialogComponent, {
      minWidth: '600px',
      maxWidth: '600px',
      data: {
        parentId: this.parentId,
        driveId: this.driveId
      },
      disableClose: true
    });

    dialogRef.afterClosed().pipe(takeUntil(this.ngUnsubscribe)).subscribe((result: FormData) => {
      if (result) {
        this.store.dispatch(new UploadFileByParentAction(result, this.parentId, this.parentId, this.sourceKey,
          this.parentId, this.invalidateSubject, {
            driveId: this.driveId
          }));
      }
    });
  }

  showFileDialog() {
    const dialogRef = this.dialog.open(FileDialogComponent, {
      minWidth: '600px',
      maxWidth: '600px',
      data: {
        itemsUrlIndex: 1,
        type: this.folderId ? 'DirectoryEntries' : 'SharedDrive',
        folderId: this.folderId,
        driveId: this.driveId,
        isFileSelector: true
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.ngUnsubscribe)).subscribe((result: FormData) => {

    });

  }

  showFolderDialog() {
    const dialogRef = this.dialog.open(FileDialogComponent, {
      minWidth: '600px',
      maxWidth: '600px',
      data: {
        itemsUrlIndex: 1,
        type: this.folderId ? 'DirectoryEntries' : 'SharedDrive',
        folderId: this.folderId,
        driveId: this.driveId,
        isFileSelector: false
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.ngUnsubscribe)).subscribe((result: FormData) => {

    });

  }

  onGridCellValueChange($event: GridValueChangeEvent) {
    if ($event.action) {
      switch ($event.action.id) {
        case 'Download':
          this.executeDownloadFileCommand($event.item.id);
          break;
        case 'Share':
          this.openShareDialog($event);
          break;
        case 'Comment':
          this.openCommentDialog($event);
          break;
        case 'Delete':
          this.openDeleteConfirmationDialog($event);
          break;
        case 'CheckIn':
          this.openCheckInDialog($event);
          break;
        case 'Checkout':
          this.openCheckOutDialog($event);
          break;
        case 'CancelCheckOut':
          this.openCancelCheckOutDialog($event);
          break;
        case 'Rename':
          this.openRenameDialog($event);
          break;
        case 'Versions':
          this.openVersionsDialog($event);
          break;
        case 'Translate':
          this.addTranslation($event);
          break;
      }
    } else if ($event.item.cells) {
      this.itemEmited($event);
    }
  }

  openShareDialog($event) {
    const model = new MemberDialogModel();
    model.invalidateSubject = this.invalidateSubject;
    model.configKey = 'DirectoryEntrySharedMembersKey';
    model.itemId = $event.item.id;

    this.fileManagerService.getSharedMembersbyDirectoryId($event.item.id).pipe(take(1)).subscribe(item => {
      if (item) {
        const selectedUsers = this.shareItemService.mapSelectedUsers(item.userGroups);
        const contactsMetaData = {
          'selectedUsers': selectedUsers,
          'permissions': item.permissions,
          'defaultPermission': this.defualtBoardPermisssion
        };
        const command = new ShareFileCommand($event.item.id, []);
        this.shareItemService.showDialog(model, contactsMetaData, command);
        /*dialogRef = this.dialog.open(ShareItemsComponent, {
          minWidth: '640px',
          maxWidth: '640px',
          data: {
            showTaskManagerRole: true,
            queryKey: 'DirectoryEntrySharedMembersKey',
            itemId: $event.item.id,
            metaData: contactsMetaData
          }
        });*/
      }
    });


    /*dialogRef.afterClosed().pipe(takeUntil(this.ngUnsubscribe)).subscribe((result: any) => {
      console.log(result);
      if (!result) {
        return;
      }
      if (result.selection.length) {
        const command = new ShareFileCommand(result.itemId, result.selection);

        this.commandService.executeCommand(command).pipe().subscribe(data => {
          console.log('ShareFileCommand finished');
          this.invalidateSubject.invalidate();
        });
      }
    });*/
  }

  openCommentDialog($event) {
    const dialogModel = DialogModel.create($event.item.cells.Name.displayText, 'CommentDialogViewComponent', {
      contextPath: $event.item.id,
    });
    dialogModel.maxWidth = '800px';
    dialogModel.minWidth = '800px';
    dialogModel.okCaption = 'Done';
    this.dialogService.showOkCancelDialog(dialogModel).pipe().subscribe(state => {
      console.log('Received', state);
    });
  }

  openDeleteConfirmationDialog($event) {
    const directoryEntryType = ($event.item.cells.IsFolder.value) ? 'folder' : 'file';
    const message = ($event.item.cells.MemberCount.value)
      ? `This ${directoryEntryType} has been shared with ${$event.item.cells.MemberCount.displayText}. Are you sure you want to delete?`
      : 'Are you sure you want to delete?';
    this.dialogService.showYesNoDialog('Confirm Delete', message).pipe().subscribe(state => {
      if (state && state.action === 'OK') {
        console.log('Received', state);
        const command = new PlatformCommand('DeleteFileOrFolderCommand', 'FileStoreApi');
        command.setValue('DirectoryEntryId', $event.item.id);
        command.fieldValues['EventSourceId'] = $event.item.parentId;
        command.fieldValues['IsEventSourceCommand'] = true;
        command.setValue('IsClientDrive', this.isClientFile());
        this.commandService.executePlatformCommand(command).pipe().subscribe(data => {
          this.invalidateSubject.invalidate();
        });
      }
    });
  }

  openCheckInDialog($event) {
    const dialogModel = DialogModel.create('Check-In', 'CheckInDialogComponent', {
      contextPath: $event.item.id,
    });
    dialogModel.okCaption = 'Check-In';
    this.dialogService.showOkCancelDialog(dialogModel).pipe().subscribe(state => {
      if (state && state.action === 'OK') {
        const originalExtension = StringUtility.getFileExtension($event.item.cells.Name.value.name);
        const newExtension = StringUtility.getFileExtension(state.data.input.fileToUpload.name);
        if (originalExtension === newExtension) {
          const formData = new FormData();
          formData.append('file', state.data.input.fileToUpload);
          formData.append('fileName', $event.item.cells.Name.value.name);
          formData.append('parentId', this.parentId);
          formData.append('driveId', this.driveId);
          formData.append('overwrite', 'true');
          this.fileManagerService.uploadFileWithParentId(formData, this.sourceKey).pipe().subscribe(result => {
            const command = new CheckinCheckoutFileCommand($event.item.id, this.driveId, state.data.input.message, false, this.createdBy);
            this.commandService.executeCommand(command).pipe().subscribe(data => {
              this.invalidateSubject.invalidate();
            });
          });
        } else {
          this.snackBarService.showError('File extension must be same!');
        }
      }
    });
  }

  openCheckOutDialog($event) {
    const dialogModel = DialogModel.create('Check-Out', 'CheckOutDialogComponent', {
      contextPath: $event.item.id,
    });
    dialogModel.okCaption = 'Check-Out';
    this.dialogService.showOkCancelDialog(dialogModel).pipe().subscribe(state => {
      if (state && state.action === 'OK') {
        const command = new CheckinCheckoutFileCommand($event.item.id, this.driveId, state.data.input.message, true, this.createdBy);
        this.commandService.executeCommand(command).pipe().subscribe(data => {
          this.invalidateSubject.invalidate();
          this.store.dispatch(new DownloadFileAction($event.item.id, $event.item.cells.Name.displayText, this.sourceKey));
        });
      }
    });
  }

  openVersionsDialog($event) {
    const dialogModel = DialogModel.create('Version History - ' + $event.item.cells.Name.displayText, 'VersionHistoryDialogViewComponent', {
      sourceKey: 'FileHistory',
      fileId: $event.item.id
    });
    dialogModel.maxWidth = '600px';
    dialogModel.minWidth = '600px';
    dialogModel.okCaption = 'Done';
    this.dialogService.showOkCancelDialog(dialogModel).pipe().subscribe(state => {
      console.log('Received', state);
    });
  }

  openCancelCheckOutDialog($event) {
    const dialogModel = DialogModel.create('Cancel Check-Out', 'CheckOutDialogComponent', {
      contextPath: $event.item.id,
    });
    dialogModel.okCaption = 'Cancel Check-Out';
    this.dialogService.showOkCancelDialog(dialogModel).pipe().subscribe(state => {
      if (state && state.action === 'OK') {
        const command = new CancelCheckoutFileCommand($event.item.id, this.driveId, state.data.input.message, this.createdBy);
        this.commandService.executeCommand(command).pipe().subscribe(data => {
          this.invalidateSubject.invalidate();
        });
      }
    });
  }

  openDeleteConfirmDialog($event) {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      minWidth: '400px',
      maxWidth: '640px',
      data: {
        itemId: $event.item.id
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.ngUnsubscribe)).subscribe((result: any) => {
      console.log(result);
    });
  }

  openRenameDialog($event) {
    console.log($event.item.cells);
    if ($event.item.cells.IsFolder.value === false) {
      const name = StringUtility.getFileNameExcludingExtension($event.item.cells['Name'].value.name);
      const extension = StringUtility.getFileExtension($event.item.cells['Name'].value.name);
      this.dialogService.showTextInputDialog('Name', name, extension, fileOrFolderNameMaxLength).pipe().subscribe(state => {
        if (state && state.action === 'OK') {
          const command = this.GetRenameDirectoryEntryCommand(state.data.input.text.trim() + state.data.input.extension,
            $event.item.id);
          this.commandService.executePlatformCommand(command).pipe().subscribe(data => {
            this.invalidateSubject.invalidate();
          });
        }
      });
    } else {
      this.dialogService.showTextInputDialog('Name', $event.item.cells['Name'].value.name, undefined,
        fileOrFolderNameMaxLength).pipe().subscribe(state => {
        if (state && state.action === 'OK') {
          const command = this.GetRenameDirectoryEntryCommand(state.data.input.text.trim(),
            $event.item.id);
          this.commandService.executePlatformCommand(command).pipe().subscribe(data => {
            this.invalidateSubject.invalidate();
          });
        }
      });
    }
  }

  protected isClientFile() {
    return false;
  }

  private GetRenameDirectoryEntryCommand(name: string, itemId: string) {
    const command = new PlatformCommand('RenameDirectoryEntryCommand', 'FileStoreApi');
    command.setValue('IsClientFile', this.isClientFile());
    command.setValue('DirectoryEntryId', itemId);
    command.setValue('Name', name);
    return command;
  }

  private itemEmited($event) {
    if ($event.item.cells['Type']) {
      switch ($event.item.cells['Type'].value) {
        case 'SharedDrives':
          this.router.navigateByUrl(this.sharedDrivesUrl);
          break;
        case 'SharedDrive':
          this.navigateByUrl(`${$event.id}`);
          break;
        case 'PersonalDrive':
          this.router.navigateByUrl(`drive/${$event.id}`);
          break;
        case 'SharedWithMe':
          this.router.navigateByUrl(`shared-with-me`);
          break;
      }
    } else {
      if ($event.item.cells.IsFolder.value === true) {
        if (!$event.item.cells['ItemId'] || !$event.item.cells['ItemId'].value) {
          console.error('Drive ID not found');
        } else {
          this.navigateByUrl(`${$event.item.cells['ItemId'].value}/${$event.item.id}`);
        }
      } else {
        const fileEntryId = $event.item.id;
        this.executeDownloadFileCommand(fileEntryId);
      }
    }
  }

  private executeDownloadFileCommand(fileEntryId: any) {
    const downloadFileCommand = new DownloadFileCommand(fileEntryId, '', this.isClientFile());
    this.commandService.executeCommand(downloadFileCommand).pipe().subscribe(data => {
      console.log('Download File', data);
      this.fileManagementService.downloadOneTimeSignedUrl(data.Payload['DownloadUrl'], data.Payload['FileName']);
    });
  }

  private addTranslation($event: any) {
    const form = new DriveFormModel();
    form.fileId = $event.item.id;
    form.relativePath = `${$event.item.cells['RelativePath'].value}`;
    const driveId = $event.item.cells['DriveId'].value;
    if (form.relativePath && form.relativePath.startsWith(driveId) === false) {
      form.relativePath = `${driveId}/${form.relativePath}`;
    }
    form.fileName = $event.item.cells['Name'].displayText;
    form.breadcrumbs = this.breadcrumbs;
    this.translationService.addTranslationByFileInfo(form);
  }
}
